import { useParams, Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Footer from "../home/components/Footer";
import { services } from "../../mocks/homeData";
import { useState } from "react";
import { usePageMeta } from "../../hooks/usePageMeta";

export default function ServiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);
  const [activeImage, setActiveImage] = useState(0);

  usePageMeta({
    title: service ? `${service.title} | Hangar Muhendislik` : "Hizmet Detayi | Hangar Muhendislik",
    description: service
      ? `${service.title} hizmetinin kapsamı, teknik detayları ve uygulama yaklaşımı.`
      : "Aradiginiz hizmet detayi bulunamadi.",
  });

  if (!service) {
    return (
      <div className="font-body antialiased">
        <Navbar />
        <main className="pt-32 pb-20 text-center">
          <h1 className="font-heading text-2xl text-navy mb-4">
            Hizmet bulunamadı
          </h1>
          <Link
            to="/hizmetler"
            className="inline-flex items-center gap-2 text-copper hover:text-copper-dark font-medium"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Tüm Hizmetlere Dön
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="font-body antialiased">
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative h-[360px] md:h-[480px] flex items-end overflow-hidden bg-navy">
          <div className="absolute inset-0">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover opacity-40"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/30" />
          <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pb-12 md:pb-16">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-white transition-colors">
                Anasayfa
              </Link>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <Link to="/hizmetler" className="hover:text-white transition-colors">
                Hizmetler
              </Link>
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </div>
              <span className="text-white">{service.title}</span>
            </nav>
            <div className="inline-block w-10 h-1 rounded-full bg-copper mb-4" />
            <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white">
              {service.title}
            </h1>
          </div>
        </section>

        {/* Content */}
        <section className="bg-white py-12 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Left: Main content */}
              <div className="lg:col-span-3">
                {/* Gallery */}
                <div className="mb-8 md:mb-10">
                  <div className="relative aspect-[16/10] rounded-lg overflow-hidden mb-3">
                    <img
                      src={service.gallery[activeImage]}
                      alt={`${service.title} - ${activeImage + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex gap-3">
                    {service.gallery.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`relative w-20 h-14 md:w-24 md:h-16 rounded-md overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-300 ${
                          activeImage === idx
                            ? "ring-2 ring-copper ring-offset-2"
                            : "opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${service.title} thumb ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Full description */}
                <div className="mb-10">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-navy mb-4">
                    Hizmet Hakkında
                  </h2>
                  <p className="font-body text-sm md:text-base text-gray-600 leading-relaxed">
                    {service.fullDescription}
                  </p>
                </div>

                {/* Features list */}
                <div className="mb-10">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-navy mb-5">
                    Öne Çıkan Özellikler
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.featuresList.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3 md:p-4 rounded-lg border border-gray-100 bg-gray-50/50"
                      >
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-fill text-copper" />
                        </div>
                        <span className="font-body text-sm text-gray-700 leading-snug">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div>
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-navy mb-5">
                    Kullanılan Teknolojiler
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {service.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-navy/5 text-navy font-body text-xs md:text-sm font-medium"
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-tools-line text-copper" />
                        </div>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Sidebar */}
              <div className="lg:col-span-2">
                <div className="sticky top-24 space-y-5">
                  {/* CTA Card */}
                  <div className="bg-navy rounded-lg p-5 md:p-6 text-white">
                    <h3 className="font-heading font-bold text-lg mb-2">
                      Teklif mi Alıcaksınız?
                    </h3>
                    <p className="font-body text-sm text-white/70 mb-5 leading-relaxed">
                      Bu hizmetle ilgili projeniz için özel fiyat teklifi ve danışmanlık hizmeti almak hemen iletişime geçin.
                    </p>
                    <Link
                      to="/iletisim"
                      className="block w-full text-center bg-copper hover:bg-copper-dark text-white font-body text-sm font-medium px-6 py-3.5 rounded-md transition-all duration-300 whitespace-nowrap"
                    >
                      Ücretsiz Teklif Al
                    </Link>
                  </div>

                  {/* Related services */}
                  <div className="bg-white rounded-lg border border-gray-200 p-5 md:p-6">
                    <h3 className="font-heading font-bold text-base text-navy mb-4">
                      Diğer Hizmetlerimiz
                    </h3>
                    <div className="space-y-3">
                      {services
                        .filter((s) => s.id !== service.id)
                        .map((s) => (
                          <Link
                            key={s.id}
                            to={`/hizmetler/${s.slug}`}
                            className="group flex items-center gap-3 p-2.5 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={s.image}
                                alt={s.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="font-body text-sm font-medium text-navy group-hover:text-copper transition-colors">
                                {s.title}
                              </p>
                              <p className="font-body text-xs text-gray-500 line-clamp-1">
                                {s.description}
                              </p>
                            </div>
                          </Link>
                        ))}
                    </div>
                  </div>

                  {/* Contact quick info */}
                  <div className="bg-gray-50 rounded-lg p-5 md:p-6 border border-gray-200">
                    <h3 className="font-heading font-bold text-base text-navy mb-4">
                      Hemen Arayın
                    </h3>
                    <div className="space-y-3">
                      <a
                        href="tel:05437811843"
                        className="flex items-center gap-3 text-sm text-gray-700 hover:text-copper transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-copper/10">
                          <i className="ri-phone-line text-copper" />
                        </div>
                        <span className="font-body">0543 781 18 43</span>
                      </a>
                      <a
                        href="tel:05418283394"
                        className="flex items-center gap-3 text-sm text-gray-700 hover:text-copper transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-copper/10">
                          <i className="ri-smartphone-line text-copper" />
                        </div>
                        <span className="font-body">0541 828 33 94</span>
                      </a>
                      <a
                        href="mailto:info@hangarmuhendislik.com"
                        className="flex items-center gap-3 text-sm text-gray-700 hover:text-copper transition-colors"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-copper/10">
                          <i className="ri-mail-line text-copper" />
                        </div>
                        <span className="font-body">info@hangarmuhendislik.com</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-gray-50 py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <div className="inline-block w-10 h-1 rounded-full bg-copper mb-5" />
            <h2 className="font-heading font-bold text-xl md:text-3xl text-navy mb-3">
              Projenizi Birlikte Hayata Geçirelim
            </h2>
            <p className="font-body text-sm md:text-base text-gray-600 mb-8 max-w-xl mx-auto">
              {service.title} hizmetimizle ilgili detaylı bilgi almak ve projenize özel çözüm önerilerimizi dinlemek için bize ulaşın.
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-dark text-white font-body text-sm font-medium px-8 py-4 rounded-md transition-all duration-300 hover:-translate-y-0.5 whitespace-nowrap"
            >
              Ücretsiz Teklif Al
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
